import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, TextField } from '@mui/material'
import Swal from 'sweetalert2'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Loading from '../../components/Loading'
import { isValid, handleAxiosError, handleNumber } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'

const EditWorkCenter = () => {
    const { t } = useTranslation()
    const { company_id, config, setCompany_id, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [typeOptions, setTypeOptions] = useState([])
    const [statusOptions, setStatusOptions] = useState([])

    /* form */
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [type, setType] = useState('')
    const [description, setDescription] = useState('')
    const [availableHours, setAvailableHours] = useState('')
    const [costPerHour, setCostPerHour] = useState('')
    const [status, setStatus] = useState('')

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/work-center')
    }

    const isFormValid = () => (
        isValid(code)
        && isValid(name)
        && isValid(siteId)
        && isValid(type)
        && isValid(status)
    )

    useEffect(() => {
        getWorkCenter()
    }, [params.id])

    /* real-time: refresh if another user updates this work center, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-workcenter-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-workcenter-deleted`)

        channelUpdated.bind(`${clientId}-workcenter-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getWorkCenter()
            }
        })

        channelDeleted.bind(`${clientId}-workcenter-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-workcenter-updated`)
            pusher.unsubscribe(`${clientId}-workcenter-deleted`)
        })
    }, [])

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getSites(company_id)
        }
    }, [company_id])

    useEffect(() => {
        getTypeOptions()
        getStatusOptions()
    }, [])

    const getWorkCenter = async () => {
        setIsLoading(true)

        await axios.get(`/api/work-centers/${params.id}`, config).then(res => {
            const workCenter = res.data?.data

            setCode(workCenter?.work_center_code ?? '')
            setName(workCenter?.work_center_name ?? '')
            setSiteId(workCenter?.site_id ?? '')
            setSiteName(workCenter?.site_name ?? '')
            setType(workCenter?.work_center_type ?? '')
            setDescription(workCenter?.description ?? '')
            setAvailableHours(isValid(workCenter?.available_hours) ? String(workCenter.available_hours) : '')
            setCostPerHour(isValid(workCenter?.cost_per_hour) ? String(workCenter.cost_per_hour) : '')
            setStatus(workCenter?.status ?? '')
        }).catch(({ response }) => {
            handleAxiosError({ response: response })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const getSites = async (id) => {
        await axios
            .get(`/api/sites?company_id=${id}`, config)
            .then(res => {
                setSites(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getTypeOptions = async () => {
        await axios
            .get('/api/work-center-types', config)
            .then(res => {
                setTypeOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/work-center-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const handleSiteChange = (a, b) => {
        setSiteId(b?.id || '')
        setSiteName(b?.label || '')
    }

    const handleAvailableHoursChange = (e) => {
        handleNumber({
            event: e,
            setter: (value) => setAvailableHours(Number(value) > 24 ? '24' : value),
            precision: 2
        })
    }

    const updateWorkCenter = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        const formData = {
            work_center_code: code,
            work_center_name: name,
            site_id: siteId,
            work_center_type: type,
            description: description,
            available_hours: availableHours !== '' ? Number(availableHours) : null,
            cost_per_hour: costPerHour !== '' ? Number(costPerHour).toFixed(2) : null,
            status: status
        }

        await axios.put(`/api/work-centers/${params.id}`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {t('update_x', { x: t('work_center') })} - {code}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-x-5'>
                            {/* Code */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('code')} *</p>
                                <TextField
                                    placeholder=""
                                    value={code}
                                    disabled
                                    onChange={(e) => setCode(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Name */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('name')} *</p>
                                <TextField
                                    placeholder=""
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Site */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                <Autocomplete
                                    disablePortal
                                    id="work-center-site-autocomplete"
                                    options={optionsSites}
                                    value={siteId ? { label: siteName, id: siteId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={handleSiteChange}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Type */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('type')} *</p>
                                <Autocomplete
                                    disablePortal
                                    id="work-center-type-autocomplete"
                                    options={typeOptions}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={typeOptions.find(o => o.value === type) || null}
                                    onChange={(a, b) => setType(b?.value || '')}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Available Hours */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('available_hours')}</p>
                                <TextField
                                    placeholder=""
                                    value={availableHours}
                                    onChange={handleAvailableHoursChange}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Cost / Hour */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('cost_per_hour')}</p>
                                <TextField
                                    placeholder=""
                                    value={costPerHour}
                                    onChange={(e) => handleNumber({ event: e, setter: setCostPerHour, precision: 2 })}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Status */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('status')} *</p>
                                <Autocomplete
                                    disablePortal
                                    id="work-center-status-autocomplete"
                                    options={statusOptions}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={statusOptions.find(o => o.value === status) || null}
                                    onChange={(a, b) => setStatus(b?.value || '')}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Description */}
                            <div className='w-full pb-4 lg:col-span-3'>
                                <p className="text-[14px] text-[#718096]">{t('description')}</p>
                                <TextField
                                    multiline
                                    rows={3}
                                    placeholder=""
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='w-full flex justify-end gap-3'>
                        <button type="button" disabled={!isFormValid()} onClick={updateWorkCenter} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#015D9F] hover:bg-[#003459] disabled:bg-[#B8B7BC]'>
                            <span className='pr-2'><i className="fa-solid fa-clock-rotate-left"></i></span> {t('update')}
                        </button>
                    </div>
                </div>
            </Box>
        </>
    )
}

export default EditWorkCenter
